const CONSENT_KEY = "portfolio.analytics.consent.v1";
const ATTRIBUTION_KEY = "portfolio.analytics.attribution.v1";
const PENDING_ATTRIBUTION_KEY = "portfolio.analytics.pending-attribution.v1";
const SCRIPT_ID = "oci-apm-browser-agent";

export const OCI_APM_CONFIG = Object.freeze({
  dataUploadEndpoint:
    "https://aaaadib5bfssyaaaaaaaaaexya.apm-agt.eu-milan-1.oci.oraclecloud.com",
  publicDataKey: "5YTTQBET3Y3OQ757KXJWROPWGYSNYIKI",
  serviceName: "cv-portfolio-browser",
  webApplication: "CV Portfolio",
  productionHost: "cv.portfoliobabahs.com",
});

const runtimeState = {
  attribution: null,
};

const safeStorageRead = (storage, key) => {
  try {
    return storage?.getItem(key) || null;
  } catch {
    return null;
  }
};

const safeStorageWrite = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
  } catch {
    // Analytics must never interfere with the portfolio if storage is unavailable.
  }
};

const cleanDimension = (value, fallback = "not_set") => {
  const cleaned = `${value || ""}`
    .trim()
    .replace(/[^a-zA-Z0-9._ /-]/g, "_")
    .slice(0, 120);
  return cleaned || fallback;
};

const referrerHost = (referrer) => {
  if (!referrer) return "direct";
  try {
    return cleanDimension(new URL(referrer).hostname, "direct");
  } catch {
    return "direct";
  }
};

const entryCampaign = (pathname = "") => {
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const match = normalizedPath.match(/^\/from\/([^/]+)$/);
  if (!match) return null;

  let source;
  try {
    source = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  if (source.includes("/")) return null;

  const cleanedSource = cleanDimension(source, "");
  if (!cleanedSource) return null;
  return { source: cleanedSource, medium: "not_set", campaign: "not_set" };
};

export const isPublicAnalyticsRoute = (pathname = "") =>
  !["/manage", "/login", "/authorize"].some(
    (privatePath) => pathname === privatePath || pathname.startsWith(`${privatePath}/`)
  );

export const isProductionAnalyticsHost = (hostname = "") =>
  hostname === OCI_APM_CONFIG.productionHost;

export const isDevelopmentAttribution = (attribution) =>
  attribution?.source?.trim().toLowerCase() === "laptop" ||
  attribution?.medium?.trim().toLowerCase() === "dev";

export const getAnalyticsConsent = (storage = window.localStorage) => {
  const value = safeStorageRead(storage, CONSENT_KEY);
  return value === "accepted" || value === "declined" ? value : "unknown";
};

export const saveAnalyticsConsent = (value, storage = window.localStorage) => {
  if (value !== "accepted" && value !== "declined") {
    throw new Error("Analytics consent must be accepted or declined");
  }
  safeStorageWrite(storage, CONSENT_KEY, value);
};

export const resolveAttribution = ({
  search = "",
  pathname = "/",
  referrer = "",
  storage = window.sessionStorage,
} = {}) => {
  const params = new URLSearchParams(search);
  const mappedCampaign = entryCampaign(pathname);
  const referringHost = referrerHost(referrer);
  const fallbackSource =
    referringHost === OCI_APM_CONFIG.productionHost ? "direct" : referringHost;
  const attribution = {
    source: cleanDimension(params.get("utm_source"), mappedCampaign?.source || fallbackSource),
    medium: cleanDimension(
      params.get("utm_medium"),
      mappedCampaign?.medium || (fallbackSource === "direct" ? "none" : "referral")
    ),
    campaign: cleanDimension(params.get("utm_campaign"), mappedCampaign?.campaign),
    content: cleanDimension(params.get("utm_content")),
    term: cleanDimension(params.get("utm_term")),
    referrerHost: referringHost,
    entryPath: cleanDimension(pathname, "/"),
  };
  safeStorageWrite(storage, ATTRIBUTION_KEY, JSON.stringify(attribution));
  return attribution;
};

export const capturePendingEntryAttribution = ({
  pathname = "/",
  search = "",
  referrer = "",
  storage = window.sessionStorage,
} = {}) => {
  if (!entryCampaign(pathname)) return null;

  const attribution = resolveAttribution({ pathname, search, referrer, storage: null });
  safeStorageWrite(storage, PENDING_ATTRIBUTION_KEY, JSON.stringify(attribution));
  return attribution;
};

export const getPendingEntryAttribution = (storage = window.sessionStorage) => {
  const stored = safeStorageRead(storage, PENDING_ATTRIBUTION_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    storage?.removeItem?.(PENDING_ATTRIBUTION_KEY);
    return null;
  }
};

export const clearPendingEntryAttribution = (storage = window.sessionStorage) => {
  try {
    storage?.removeItem?.(PENDING_ATTRIBUTION_KEY);
  } catch {
    // Analytics must never interfere with the portfolio if storage is unavailable.
  }
};

const deleteTrackingCookie = () => {
  document.cookie = "OciTrack=; Path=/; Max-Age=0; SameSite=Lax; Secure";
};

export const disableOciApm = () => {
  if (window.apmrum) {
    window.apmrum.obs = 0;
    window.apmrum.allowCreateCookie = false;
  }
  deleteTrackingCookie();
};

export const enableOciApm = ({
  pathname,
  search,
  referrer = document.referrer,
  attribution = null,
} = {}) => {
  if (!isProductionAnalyticsHost(window.location.hostname) || !isPublicAnalyticsRoute(pathname)) {
    disableOciApm();
    return false;
  }

  const entryAttribution =
    attribution || resolveAttribution({ pathname, search, referrer });
  if (isDevelopmentAttribution(entryAttribution)) {
    runtimeState.attribution = null;
    clearPendingEntryAttribution();
    disableOciApm();
    return false;
  }

  // Attribution belongs to the page entry. Keep it stable while React changes routes,
  // but do not revive a campaign from a previous full-page visit or restored tab.
  runtimeState.attribution ||= entryAttribution;
  safeStorageWrite(
    window.sessionStorage,
    ATTRIBUTION_KEY,
    JSON.stringify(runtimeState.attribution)
  );
  clearPendingEntryAttribution();
  window.apmrum = window.apmrum || {};
  window.apmrum.serviceName = OCI_APM_CONFIG.serviceName;
  window.apmrum.webApplication = OCI_APM_CONFIG.webApplication;
  window.apmrum.ociDataUploadEndpoint = OCI_APM_CONFIG.dataUploadEndpoint;
  window.apmrum.OracleAPMPublicDataKey = OCI_APM_CONFIG.publicDataKey;
  window.apmrum.allowCreateCookie = true;
  window.apmrum.trackScreenText = false;
  window.apmrum.obs = 0xffffffff;
  window.apmrum.udfAttributes = [
    { name: "portfolioRoute", value: () => window.location.pathname },
    { name: "utmSource", value: () => runtimeState.attribution?.source },
    { name: "utmMedium", value: () => runtimeState.attribution?.medium },
    { name: "utmCampaign", value: () => runtimeState.attribution?.campaign },
    { name: "utmContent", value: () => runtimeState.attribution?.content },
    { name: "utmTerm", value: () => runtimeState.attribution?.term },
    { name: "referrerHost", value: () => runtimeState.attribution?.referrerHost },
    { name: "entryPath", value: () => runtimeState.attribution?.entryPath },
  ];
  window.apmrum.rewriteRules = [
    { context: "query_elm", options: "g", pattern: ".+", replacement: "*" },
    { context: "fragment", options: "g", pattern: ".+", replacement: "*" },
  ];

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `${OCI_APM_CONFIG.dataUploadEndpoint}/static/jslib/apmrum.min.js`;
    document.head.appendChild(script);
  }
  return true;
};

export const analyticsStorageKeys = Object.freeze({
  attribution: ATTRIBUTION_KEY,
  consent: CONSENT_KEY,
  pendingAttribution: PENDING_ATTRIBUTION_KEY,
});
